import { z } from 'zod';
import { isoDate } from './date';
import { idempotency } from './spine';
import { listInput } from './list';
import { JOB_STAGE_OUTCOMES } from './jobPipeline';

const uuid = z.string().uuid();
const fields = {
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(4000).nullable().optional(),
  customerId: uuid.nullable().optional(),
  propertyId: uuid.nullable().optional(),
  ownerUserId: uuid.nullable().optional(),
  serviceTypeId: uuid.nullable().optional(),
  projectId: uuid.nullable().optional(),
  requestedOn: isoDate.nullable().optional(),
  requestedWindow: z.string().trim().max(200).nullable().optional(),
  expectedCloseOn: isoDate.nullable().optional(),
  priority: z.enum(['normal', 'urgent']).optional(),
};

/** A convenience entry still creates truthful intake. Existing Requests supply contact data. */
export const commercialJobCreateInput = idempotency
  .extend({
    ...fields,
    requestId: uuid.optional(),
    contactName: z.string().trim().min(1).max(200).optional(),
    contactEmail: z.string().trim().email().max(200).optional(),
    contactPhone: z.string().trim().max(40).optional(),
    pipelineId: uuid.optional(),
    stageId: uuid.optional(),
  })
  .strict()
  .refine((v) => Boolean(v.requestId || v.customerId || v.contactName), {
    message: 'Choose a Request or customer, or enter the person requesting the work',
    path: ['contactName'],
  });
export type CommercialJobCreateInput = z.infer<typeof commercialJobCreateInput>;

export const commercialJobIdInput = z.object({ commercialJobId: uuid }).strict();
export type CommercialJobIdInput = z.infer<typeof commercialJobIdInput>;
export const commercialJobLinkInput = z.object({ id: uuid }).strict();
export type CommercialJobLinkInput = z.infer<typeof commercialJobLinkInput>;
export const commercialJobUpdateInput = idempotency
  .extend({
    ...fields,
    title: fields.title.optional(),
    commercialJobId: uuid,
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type CommercialJobUpdateInput = z.infer<typeof commercialJobUpdateInput>;
export const commercialJobMoveStageInput = idempotency
  .extend({
    commercialJobId: uuid,
    pipelineId: uuid,
    stageId: uuid,
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type CommercialJobMoveStageInput = z.infer<typeof commercialJobMoveStageInput>;
export const commercialJobListInput = listInput
  .extend({
    pipelineId: uuid.optional(),
    stageId: uuid.optional(),
    ownerUserId: uuid.optional(),
    serviceTypeId: uuid.optional(),
    outcome: z.enum(JOB_STAGE_OUTCOMES).optional(),
  })
  .strict();
export type CommercialJobListInput = z.infer<typeof commercialJobListInput>;

export const formatCommercialJobNumber = (n: number): string => `JOB-${n}`;

export const JOB_ACTIVITY_KINDS = ['note', 'call', 'task', 'meeting'] as const;
export const JOB_LAYOUT_CARDS = [
  'details',
  'customer',
  'property',
  'quotes',
  'work_orders',
  'attachments',
  'custom_fields',
] as const;
export type JobLayoutCard = (typeof JOB_LAYOUT_CARDS)[number];
const cardKeys = z
  .array(z.enum(JOB_LAYOUT_CARDS))
  .max(JOB_LAYOUT_CARDS.length)
  .refine((v) => new Set(v).size === v.length, { message: 'Each card must appear once' });
export const commercialJobActivityCreateInput = idempotency
  .extend({
    commercialJobId: uuid,
    kind: z.enum(JOB_ACTIVITY_KINDS),
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().max(10000).optional(),
    occurredAt: z.coerce.date().optional(),
    dueAt: z.coerce.date().optional(),
    assignedUserId: uuid.optional(),
  })
  .strict();
export type CommercialJobActivityCreateInput = z.infer<
  typeof commercialJobActivityCreateInput
>;
export const commercialJobActivityCompleteInput = idempotency
  .extend({
    activityId: uuid,
    expectedVersion: z.number().int().positive(),
  })
  .strict();
export type CommercialJobActivityCompleteInput = z.infer<
  typeof commercialJobActivityCompleteInput
>;
export const commercialJobActivityListInput = listInput
  .extend({ commercialJobId: uuid, kind: z.enum(JOB_ACTIVITY_KINDS).optional() })
  .strict();
export type CommercialJobActivityListInput = z.infer<
  typeof commercialJobActivityListInput
>;
export const jobLayoutUpdateInput = idempotency
  .extend({
    expectedVersion: z.number().int().positive(),
    cardOrder: cardKeys.refine((v) => v.length === JOB_LAYOUT_CARDS.length, {
      message: 'Include every predefined card',
    }),
    hiddenCards: cardKeys,
  })
  .strict();
export type JobLayoutUpdateInput = z.infer<typeof jobLayoutUpdateInput>;
/** Personal choices cannot restore a card the tenant has hidden. User identity comes from the session. */
export const jobViewPreferenceUpdateInput = z
  .object({
    view: z.enum(['list', 'pipeline']).optional(),
    pipelineId: uuid.nullable().optional(),
    cardOrder: cardKeys.optional(),
    hiddenCards: cardKeys.optional(),
  })
  .strict();
export type JobViewPreferenceUpdateInput = z.infer<typeof jobViewPreferenceUpdateInput>;
